import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography } from '@material-ui/core';
import { themePalette } from '../../utilities/branding';
import { isBrokerage } from '../../utilities/brokerage_utils';
import { agentConnectTerms, brokerConnectTerms } from '../../assets/text/termsAndConditions';

const useStyles = makeStyles(() => ({
	page: {
		minHeight: '100vh',
		backgroundColor: themePalette.secondary_background,
		boxSizing: 'border-box',
	},
	header: {
		backgroundColor: themePalette.header_bar,
		padding: '16px 24px',
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	body: {
		padding: '24px 16px',
	},
	card: {
		maxWidth: 900,
		margin: '0 auto',
	},
	content: {
		textAlign: 'left',
	},
}));

// Read-only, permission-less terms page so the privacy banner links resolve pre-login.
export const PublicTermsAndConditions: React.FC = () => {
	const classes = useStyles();
	const location = useLocation();
	const headerRef = useRef<HTMLDivElement>(null);
	const markup = { __html: isBrokerage ? brokerConnectTerms : agentConnectTerms };

	// Rise's hash router keeps the route in the URL hash, so the target section arrives as a query param.
	// Content is injected via dangerouslySetInnerHTML and the app resets scroll to top on load, so retry
	// scrolling a few times, offsetting by the sticky header height so the section title stays visible.
	useEffect(() => {
		const id = new URLSearchParams(location.search).get('section');
		if (!id) {
			return;
		}
		const scrollToSection = () => {
			const target = document.getElementById(id);
			if (!target)
				return;
			const headerHeight = headerRef.current?.offsetHeight ?? 0;
			target.style.scrollMarginTop = `${headerHeight}px`;
			target.scrollIntoView();
		};
		const timers = [0, 100, 300, 600].map(delay => window.setTimeout(scrollToSection, delay));
		return () => timers.forEach(timer => window.clearTimeout(timer));
	}, [location.search]);

	return (
		<div className={classes.page}>
			<div ref={headerRef} className={classes.header}>
				<Typography variant="h5" style={{ color: themePalette.negative_text }}>
					Terms of Use &amp; Privacy Policy
				</Typography>
			</div>
			<div className={classes.body}>
				<Card className={classes.card}>
					<CardContent className={classes.content}>
						<div dangerouslySetInnerHTML={markup} />
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default PublicTermsAndConditions;
